import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Activity, Wifi, WifiOff, AlertTriangle, CheckCircle2, AlertOctagon, Brain, Building2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

type NodeStatus = "ok" | "warn" | "error" | "idle";

interface FlowNode {
  id: string;
  label: string;
  sub?: string;
  x: number;
  y: number;
  status: NodeStatus;
  detail?: string;
}

interface FlowEdge {
  from: string;
  to: string;
  active?: boolean;
  variant?: "primary" | "influence";
  label?: string;
}

const STATUS_COLORS: Record<NodeStatus, { fill: string; stroke: string; text: string; ring: string }> = {
  ok:    { fill: "hsl(var(--primary) / 0.15)",    stroke: "hsl(var(--primary))",     text: "hsl(var(--primary))",     ring: "hsl(var(--primary) / 0.4)" },
  warn:  { fill: "hsl(var(--warning, 38 92% 50%) / 0.15)", stroke: "hsl(38 92% 50%)", text: "hsl(38 92% 50%)",          ring: "hsl(38 92% 50% / 0.4)" },
  error: { fill: "hsl(var(--destructive) / 0.15)", stroke: "hsl(var(--destructive))", text: "hsl(var(--destructive))", ring: "hsl(var(--destructive) / 0.4)" },
  idle:  { fill: "hsl(var(--muted) / 0.5)",        stroke: "hsl(var(--muted-foreground) / 0.4)", text: "hsl(var(--muted-foreground))", ring: "hsl(var(--muted-foreground) / 0.2)" },
};

export default function RealtimeFlow() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>();
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();
  const [flagBusy, setFlagBusy] = useState<string | null>(null);
  const orgId = selectedOrgId;

  // Role do usuário na org selecionada (para autorizar toggles)
  const { data: orgRole } = useQuery({
    queryKey: ["realtime-flow", "role", user?.id, orgId],
    enabled: !!user?.id && !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("org_roles")
        .select("role")
        .eq("user_id", user!.id)
        .eq("org_id", orgId!)
        .maybeSingle();
      return (data?.role as string) ?? "viewer";
    },
    staleTime: 5 * 60_000,
  });
  const canManage = ["owner", "admin", "superadmin"].includes(orgRole ?? "");

  async function toggleFlag(flagKey: string, nextEnabled: boolean) {
    if (!orgId || !canManage) return;
    setFlagBusy(flagKey);
    const { error } = await supabase
      .from("feature_flags")
      .upsert(
        {
          org_id: orgId,
          flag_key: flagKey,
          enabled: nextEnabled,
          rollout_pct: nextEnabled ? 100 : 0,
        },
        { onConflict: "org_id,flag_key" },
      );
    setFlagBusy(null);
    if (error) {
      toast.error(`Falha ao atualizar ${flagKey}: ${error.message}`);
      return;
    }
    toast.success(`${flagKey} ${nextEnabled ? "ativado" : "desativado"}`);
    qc.invalidateQueries({ queryKey: ["realtime-flow", "cortex-flags", orgId] });
  }

  // Lista de organizações às quais o usuário pertence
  const { data: orgs } = useQuery({
    queryKey: ["realtime-flow", "orgs"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("org_members")
        .select("org_id, organizations:org_id(id, name)")
        .eq("user_id", user.id);
      if (error) throw new Error(error.message);
      return (data ?? [])
        .map((r: any) => r.organizations)
        .filter(Boolean) as Array<{ id: string; name: string }>;
    },
    staleTime: 10 * 60 * 1000,
  });

  // Auto-seleciona a primeira org quando carrega
  useEffect(() => {
    if (!selectedOrgId && orgs && orgs.length > 0) {
      setSelectedOrgId(orgs[0].id);
    }
  }, [orgs, selectedOrgId]);

  // Reset group quando troca de org
  useEffect(() => {
    setSelectedGroupId(undefined);
  }, [selectedOrgId]);

  // Lista de grupos
  const { data: groups } = useQuery({
    queryKey: ["realtime-flow", "groups", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name, instance_status, active")
        .eq("org_id", orgId!)
        .eq("active", true)
        .order("name");
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  // Última execução do pipeline para o grupo selecionado (polling)
  const { data: lastExec, dataUpdatedAt } = useQuery({
    queryKey: ["realtime-flow", "last-exec", selectedGroupId],
    enabled: !!selectedGroupId && !!orgId,
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skill_feedback")
        .select("id, created_at, skill_name, intent, intent_confidence, execution_time_ms, quality_score, was_sent_to_group, model_used, memory_used_count, output_length")
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .eq("is_test", false)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  // Provider health (híbrido): combina syra_ai_providers (base = configurados) com provider_health (status runtime)
  const { data: providerHealth } = useQuery({
    queryKey: ["realtime-flow", "provider-health", orgId],
    enabled: !!orgId,
    refetchInterval: 10_000,
    queryFn: async () => {
      const [healthRes, configRes] = await Promise.all([
        supabase
          .from("provider_health")
          .select("provider_name, status, last_error_code, avg_latency_ms")
          .eq("org_id", orgId!),
        supabase
          .from("syra_ai_providers_safe")
          .select("provider_name, active, last_test_ok, last_tested_at")
          .eq("active", true),
      ]);
      if (healthRes.error) throw new Error(healthRes.error.message);
      if (configRes.error) throw new Error(configRes.error.message);

      const runtime = new Map(
        (healthRes.data ?? []).map((p) => [p.provider_name.toLowerCase(), p]),
      );

      // Base = provedores cadastrados; sobrepõe com runtime quando existe
      return (configRes.data ?? []).map((cfg) => {
        const rt = runtime.get(cfg.provider_name.toLowerCase());
        if (rt) {
          return {
            provider_name: cfg.provider_name,
            status: rt.status,
            last_error_code: rt.last_error_code,
            avg_latency_ms: rt.avg_latency_ms,
          };
        }
        // Sem dados de runtime → usa último teste manual como proxy
        return {
          provider_name: cfg.provider_name,
          status: cfg.last_test_ok === true ? "healthy" : cfg.last_test_ok === false ? "degraded" : "unknown",
          last_error_code: null,
          avg_latency_ms: null,
        };
      });
    },
  });

  // Estatística de intents (últimos 7 dias)
  const { data: intentStats = [] } = useQuery({
    queryKey: ["realtime-flow", "intent-stats", orgId, selectedGroupId],
    enabled: !!orgId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      let q = supabase
        .from("skill_feedback")
        .select("intent")
        .eq("org_id", orgId!)
        .eq("is_test", false)
        .gte("created_at", since)
        .not("intent", "is", null)
        .limit(2000);
      if (selectedGroupId) q = q.eq("group_id", selectedGroupId);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      const counts = new Map<string, number>();
      for (const r of data ?? []) {
        const k = r.intent ?? "unknown";
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      const total = (data ?? []).length;
      return Array.from(counts.entries())
        .map(([intent, count]) => ({ intent, count, pct: total ? (count / total) * 100 : 0 }))
        .sort((a, b) => b.count - a.count);
    },
  });

  // Log de erros do pipeline (últimas 24h)
  const { data: errorLog = [] } = useQuery({
    queryKey: ["realtime-flow", "error-log", orgId, selectedGroupId],
    enabled: !!orgId,
    refetchInterval: 15_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      let q = supabase
        .from("model_decisions")
        .select("id, created_at, skill_name, task_class, provider_used, model_used, error_type, reason, latency_ms")
        .eq("org_id", orgId!)
        .eq("success", false)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(50);
      if (selectedGroupId) q = q.eq("group_id", selectedGroupId);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  // ── Cortex layer queries ──────────────────────────────────────────
  // Janela de 24h para capturar eventos esparsos (threat/conflict são raros)
  const { data: cortexLast } = useQuery({
    queryKey: ["realtime-flow", "cortex-last", orgId, selectedGroupId],
    enabled: !!orgId && !!selectedGroupId,
    refetchInterval: 5000,
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("cortex_events")
        .select("event_type, payload, occurred_at")
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .in("event_type", ["threat.detected", "conflict.detected", "input.gating.decided"])
        .gte("occurred_at", since)
        .order("occurred_at", { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as Array<{
        event_type: string;
        payload: Record<string, unknown> | null;
        occurred_at: string;
      }>;
      // Normaliza para created_at para o resto do código
      return rows.map((r) => ({
        event_type: r.event_type,
        payload: r.payload,
        created_at: r.occurred_at,
        source: null as string | null,
      }));
    },
  });

  // Contagem real de memória disponível para o grupo (não memory_used_count que está sempre 0)
  const { data: memoryStats } = useQuery({
    queryKey: ["realtime-flow", "memory-stats", orgId, selectedGroupId],
    enabled: !!orgId && !!selectedGroupId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { count } = await supabase
        .from("consolidated_memories")
        .select("*", { count: "exact", head: true })
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .eq("active", true);
      return { total: count ?? 0 };
    },
  });

  const { data: inhLast } = useQuery({
    queryKey: ["realtime-flow", "inh-last", orgId, selectedGroupId],
    enabled: !!orgId && !!selectedGroupId,
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inhibition_decisions")
        .select("action, reason, group_state, arousal, created_at")
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const { data: tomStats } = useQuery({
    queryKey: ["realtime-flow", "tom", orgId, selectedGroupId],
    enabled: !!orgId,
    refetchInterval: 15_000,
    queryFn: async () => {
      let q = supabase
        .from("tom_models")
        .select("id, updated_at, observation_count", { count: "exact" })
        .eq("org_id", orgId!)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (selectedGroupId) q = q.eq("group_id", selectedGroupId);
      const { data, count, error } = await q;
      if (error) throw new Error(error.message);
      return { count: count ?? 0, lastUpdatedAt: data?.[0]?.updated_at ?? null };
    },
  });

  const { data: affectLast } = useQuery({
    queryKey: ["realtime-flow", "affect", orgId, selectedGroupId],
    enabled: !!orgId && !!selectedGroupId,
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affect_states")
        .select("valence, arousal, dominance, created_at")
        .eq("org_id", orgId!)
        .eq("group_id", selectedGroupId!)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const { data: cortexFlags } = useQuery({
    queryKey: ["realtime-flow", "cortex-flags", orgId],
    enabled: !!orgId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const keys = [
        "cortex_full_loop_enabled",
        "cortex_threat_detection",
        "cortex_conflict_detection",
        "cortex_input_gating",
        "cortex.tom",
        "cortex.affect",
      ];
      const { data, error } = await supabase
        .from("feature_flags")
        .select("flag_key, enabled, rollout_pct")
        .eq("org_id", orgId!)
        .in("flag_key", keys);
      if (error) throw new Error(error.message);
      return Object.fromEntries((data ?? []).map((r) => [r.flag_key, r])) as Record<
        string,
        { flag_key: string; enabled: boolean; rollout_pct: number | null }
      >;
    },
  });

  const flagOn = (key: string) => {
    const f = cortexFlags?.[key];
    return !!f?.enabled;
  };

  const selectedGroup = groups?.find((g) => g.id === selectedGroupId);

  const { nodes, edges, gateDecision, inhibitorAction } = useMemo<{
    nodes: FlowNode[];
    edges: FlowEdge[];
    gateDecision: string | null;
    inhibitorAction: string | null;
  }>(() => {
    const providers = providerHealth ?? [];
    const isOk = (s: string) => s === "ok" || s === "healthy";
    const isWarn = (s: string) => s === "slow" || s === "rate_limited" || s === "degraded";
    const isError = (s: string) => s === "down" || s === "budget_exceeded";

    const llmAnyOk = providers.some((p) => isOk(p.status));
    const llmAnyWarn = providers.some((p) => isWarn(p.status));
    const llmAllError = providers.length > 0 && providers.every((p) => isError(p.status));

    const llmStatus: NodeStatus =
      llmAllError ? "error"
      : llmAnyWarn && !llmAnyOk ? "error"
      : llmAnyWarn ? "warn"
      : llmAnyOk ? "ok"
      : "idle";

    const instStatus: NodeStatus =
      !selectedGroup ? "idle"
        : selectedGroup.instance_status === "connected" ? "ok"
        : selectedGroup.instance_status === "qr_pending" ? "warn"
        : "error";

    const recentMs = lastExec ? Date.now() - new Date(lastExec.created_at).getTime() : Infinity;
    const isRecent = recentMs < 60_000;

    const skillExecuted = !!lastExec;
    const skillSent = lastExec?.was_sent_to_group === true;

    const skillStatus: NodeStatus =
      !selectedGroupId ? "idle"
      : !skillExecuted ? "idle"
      : (lastExec?.quality_score ?? 1) < 0.5 ? "warn"
      : "ok";

    const memoryTotal = memoryStats?.total ?? 0;
    const memoryStatus: NodeStatus =
      !selectedGroupId ? "idle"
      : memoryTotal > 0 ? "ok"
      : "idle";

    // ── Cortex layer derivations ──────────────────────────────────
    const events = cortexLast ?? [];
    const threatEvents = events.filter((e) => e.event_type === "threat.detected");
    const conflictEvents = events.filter((e) => e.event_type === "conflict.detected");
    const gateEvents = events.filter((e) => e.event_type === "input.gating.decided");
    const lastThreat = threatEvents[0];
    const lastConflict = conflictEvents[0];
    const lastGate = gateEvents[0];
    const gateDecisionVal = (lastGate?.payload as Record<string, unknown> | null)?.decision as
      | string
      | undefined ?? null;

    const fmtAge = (iso?: string) => {
      if (!iso) return null;
      const ms = Date.now() - new Date(iso).getTime();
      const m = Math.floor(ms / 60000);
      if (m < 1) return "agora";
      if (m < 60) return `há ${m}min`;
      const h = Math.floor(m / 60);
      return `há ${h}h`;
    };

    const fullLoopOn = flagOn("cortex_full_loop_enabled");
    const threatFlagOn = flagOn("cortex_threat_detection") && fullLoopOn;
    const conflictFlagOn = flagOn("cortex_conflict_detection") && fullLoopOn;
    const gateFlagOn = flagOn("cortex_input_gating") && fullLoopOn;
    const tomFlagOn = flagOn("cortex.tom");
    const affectFlagOn = flagOn("cortex.affect");

    // Status considera evento recente (≤30min) para warn, qualquer evento 24h para ok
    const threatRecent = lastThreat && Date.now() - new Date(lastThreat.created_at).getTime() < 30 * 60_000;
    const threatStatus: NodeStatus = !threatFlagOn
      ? "idle"
      : threatRecent
      ? "warn"
      : threatEvents.length || gateEvents.length
      ? "ok"
      : "idle";
    const conflictScore = (lastConflict?.payload as Record<string, unknown> | null)?.score as number | undefined;
    const conflictRecent = lastConflict && Date.now() - new Date(lastConflict.created_at).getTime() < 30 * 60_000;
    const conflictStatus: NodeStatus = !conflictFlagOn
      ? "idle"
      : conflictRecent && (conflictScore ?? 0) >= 0.5
      ? "warn"
      : conflictEvents.length || gateEvents.length
      ? "ok"
      : "idle";
    const gateRecent = lastGate && Date.now() - new Date(lastGate.created_at).getTime() < 10 * 60_000;
    const gateStatus: NodeStatus = !gateFlagOn
      ? "idle"
      : gateRecent && gateDecisionVal === "drop"
      ? "error"
      : gateRecent && gateDecisionVal === "throttle"
      ? "warn"
      : gateEvents.length
      ? "ok"
      : "idle";

    // Inhibitor
    const inhAction = inhLast?.action ?? null;
    const inhStatus: NodeStatus =
      !inhAction ? "idle"
      : inhAction === "send" ? "ok"
      : ["shorten", "rephrase", "replace_with_neutral", "ask_clarification"].includes(inhAction) ? "warn"
      : "error";

    // ToM
    const tomCount = tomStats?.count ?? 0;
    const tomAgeMs = tomStats?.lastUpdatedAt
      ? Date.now() - new Date(tomStats.lastUpdatedAt).getTime()
      : Infinity;
    const tomStatus: NodeStatus = !tomFlagOn
      ? "idle"
      : tomCount === 0
      ? "idle"
      : tomAgeMs < 10 * 60_000
      ? "ok"
      : tomAgeMs < 30 * 60_000
      ? "ok"
      : "warn";

    // Affect
    const affectStatus: NodeStatus = !affectFlagOn ? "idle" : affectLast ? "ok" : "idle";
    const affectSub = affectLast
      ? `V:${Number(affectLast.valence).toFixed(2)} A:${Number(affectLast.arousal).toFixed(2)} D:${Number(affectLast.dominance).toFixed(2)}`
      : "—";

    // Output / response
    const turnDropped = gateDecisionVal === "drop";
    const isSilencing = inhAction === "block" || inhAction === "log_and_silence";
    const isEscalated = inhAction === "escalate_to_human";
    const isRewritten = inhAction && ["shorten", "rephrase", "replace_with_neutral", "ask_clarification"].includes(inhAction);
    const outSub = turnDropped
      ? "turno abortado"
      : isSilencing
      ? "silenciada"
      : isEscalated
      ? "escalada"
      : isRewritten
      ? "reescrita"
      : skillSent
      ? "enviada"
      : lastExec
      ? "não enviada"
      : "—";
    const outStatus: NodeStatus =
      turnDropped || isSilencing ? "error"
      : isEscalated || isRewritten ? "warn"
      : !lastExec ? "idle"
      : skillSent ? "ok"
      : "warn";

    const flagBadge = (on: boolean) => (on ? "" : " · flag off");

    const ns: FlowNode[] = [
      // Faixa central (y=300)
      { id: "wa", label: "WhatsApp", sub: "Evolution API", x: 80, y: 300, status: instStatus,
        detail: selectedGroup ? `Instância: ${selectedGroup.instance_status ?? "—"}` : "Selecione um grupo" },
      { id: "wh", label: "Webhook", sub: "whatsapp-webhook", x: 230, y: 300, status: instStatus === "error" ? "error" : "ok" },
      { id: "pl", label: "Pipeline", sub: "webhook-pipeline", x: 380, y: 300, status: isRecent ? "ok" : "idle",
        detail: lastExec ? `Última: ${new Date(lastExec.created_at).toLocaleTimeString()}` : "Sem mensagens recentes" },
      { id: "mem", label: "Memória", sub: `${memoryTotal} ${memoryTotal === 1 ? "item" : "itens"}`, x: 530, y: 300, status: memoryStatus,
        detail: memoryTotal > 0
          ? `${memoryTotal} memórias consolidadas ativas no grupo${(lastExec?.memory_used_count ?? 0) > 0 ? ` · último turno usou ${lastExec?.memory_used_count}` : ""}`
          : "Sem memórias consolidadas — aguardando consolidação" },
      { id: "sr", label: "Skill Router", sub: lastExec?.skill_name ?? "—", x: 680, y: 300, status: skillStatus,
        detail: lastExec ? `${lastExec.execution_time_ms}ms • Q:${(lastExec.quality_score ?? 0).toFixed(2)}` : undefined },
      { id: "llm", label: "LLM Gateway", sub: lastExec?.model_used ?? "model-gateway", x: 830, y: 300, status: llmStatus,
        detail: providerHealth?.length
          ? providerHealth.map((p) => `${p.provider_name}: ${p.status}`).join(" • ")
          : "Sem dados de saúde" },
      { id: "inh", label: "Inhibitor", sub: inhAction ?? "—", x: 1130, y: 300, status: inhStatus,
        detail: inhLast
          ? `${inhAction} · estado=${inhLast.group_state ?? "—"} · arousal=${Number(inhLast.arousal ?? 0).toFixed(2)}\n${inhLast.reason ?? ""}`
          : "Sem decisões recentes" },
      { id: "out", label: "Resposta", sub: outSub, x: 1300, y: 300, status: outStatus },

      // Faixa superior (y=90) — filtros paralelos
      { id: "intent", label: "Intent", sub: lastExec?.intent ?? "—", x: 530, y: 90, status: lastExec?.intent ? "ok" : "idle",
        detail: lastExec?.intent ? `${lastExec.intent} (${Math.round((lastExec.intent_confidence ?? 0) * 100)}%)` : undefined },
      { id: "sent", label: "Sentimento", sub: "sentiment-score", x: 680, y: 90, status: lastExec ? "ok" : "idle" },
      { id: "tom", label: "Theory of Mind", sub: tomFlagOn ? `${tomCount} perfis` : `flag off`, x: 830, y: 90, status: tomStatus,
        detail: tomFlagOn
          ? `Modelo per-membro (EWMA α=0.2)\n${tomCount} perfis · ${tomStats?.lastUpdatedAt ? `atualizado ${new Date(tomStats.lastUpdatedAt).toLocaleTimeString()}` : "sem updates"}\nTraços: formality, verbosity, sentiment_baseline, tópicos`
          : "Flag cortex.tom desligada — não está executando." },
      { id: "affect", label: "Affect (VAD)", sub: affectSub + flagBadge(affectFlagOn), x: 980, y: 90, status: affectStatus,
        detail: affectFlagOn
          ? affectLast
            ? `V=${Number(affectLast.valence).toFixed(2)} · A=${Number(affectLast.arousal).toFixed(2)} · D=${Number(affectLast.dominance).toFixed(2)}\nJanela 30 min · consome sentiment_baseline do ToM`
            : "Sem snapshot recente"
          : "Flag cortex.affect desligada." },

      // Faixa inferior (y=510) — Cortex filters (janela 24h)
      { id: "threat", label: "Threat Detect",
        sub: !threatFlagOn ? "flag off"
          : threatEvents.length === 0 ? "sem ameaças 24h"
          : `${threatEvents.length} em 24h${lastThreat ? ` · ${fmtAge(lastThreat.created_at)}` : ""}`,
        x: 680, y: 510, status: threatStatus,
        detail: !threatFlagOn ? "Flag cortex_threat_detection desligada."
          : threatEvents.length === 0 ? "Nenhuma detecção nas últimas 24h — pipeline saudável"
          : `${threatEvents.length} ameaça(s) nas últimas 24h\nÚltima ${fmtAge(lastThreat!.created_at)}: ${(lastThreat!.payload as Record<string, unknown> | null)?.type ?? "—"}` },
      { id: "conf", label: "Conflict Detect",
        sub: !conflictFlagOn ? "flag off"
          : conflictEvents.length === 0 ? "sem conflitos 24h"
          : `${conflictEvents.length} em 24h${lastConflict ? ` · score ${conflictScore?.toFixed(2) ?? "—"}` : ""}`,
        x: 830, y: 510, status: conflictStatus,
        detail: conflictFlagOn
          ? lastConflict ? `score=${conflictScore?.toFixed(2)} · tipo=${(lastConflict.payload as Record<string, unknown> | null)?.type ?? "—"}` : "Sem conflitos detectados"
          : "Flag cortex_conflict_detection desligada." },
      { id: "gate", label: "Input Gating",
        sub: !gateFlagOn ? "flag off"
          : gateEvents.length === 0 ? "sem decisões 24h"
          : `${gateEvents.length} em 24h${lastGate ? ` · ${gateDecisionVal ?? "—"}` : ""}`,
        x: 980, y: 510, status: gateStatus,
        detail: !gateFlagOn ? "Flag cortex_input_gating desligada."
          : gateEvents.length === 0 ? "Nenhuma decisão de gating registrada nas últimas 24h"
          : `${gateEvents.length} decisões nas últimas 24h\nÚltima ${fmtAge(lastGate!.created_at)}: ${gateDecisionVal ?? "—"}` },
    ];

    const es: FlowEdge[] = [
      // Caminho principal — usa memoryTotal (real) em vez de memory_used_count (sempre 0)
      { from: "wa", to: "wh", active: instStatus === "ok", variant: "primary" },
      { from: "wh", to: "pl", active: isRecent, variant: "primary" },
      { from: "pl", to: "mem", active: !turnDropped && memoryTotal > 0, variant: "primary" },
      { from: "mem", to: "sr", active: !turnDropped && memoryTotal > 0, variant: "primary" },
      { from: "sr", to: "llm", active: skillExecuted && llmStatus !== "error", variant: "primary" },
      { from: "llm", to: "inh", active: skillExecuted && llmStatus !== "error", variant: "primary" },
      { from: "inh", to: "out", active: !turnDropped && !isSilencing && skillSent, variant: "primary" },

      // Filtros paralelos (fire-and-forget)
      { from: "pl", to: "intent", active: !!lastExec?.intent, variant: "primary" },
      { from: "pl", to: "sent", active: !!lastExec, variant: "primary" },
      { from: "pl", to: "tom", active: tomFlagOn && tomCount > 0, variant: "primary" },

      // Cortex filters
      { from: "pl", to: "threat", active: threatFlagOn, variant: "primary" },
      { from: "pl", to: "conf", active: conflictFlagOn, variant: "primary" },
      { from: "pl", to: "gate", active: gateFlagOn, variant: "primary" },

      // Convergência no Inhibitor
      { from: "threat", to: "inh", active: threatFlagOn && !!lastThreat, variant: "primary" },
      { from: "conf", to: "inh", active: conflictFlagOn && (conflictScore ?? 0) >= 0.5, variant: "primary" },
      { from: "gate", to: "inh", active: gateFlagOn && gateDecisionVal === "drop", variant: "primary" },

      // Influências (leitura de estado)
      { from: "tom", to: "affect", active: tomFlagOn && affectFlagOn, variant: "influence", label: "influência" },
      { from: "tom", to: "llm", active: tomFlagOn, variant: "influence", label: "influência" },
      { from: "affect", to: "inh", active: affectFlagOn, variant: "influence", label: "influência" },
      { from: "intent", to: "sr", active: !!lastExec?.intent, variant: "primary" },
    ];

    return { nodes: ns, edges: es, gateDecision: gateDecisionVal, inhibitorAction: inhAction };
  }, [selectedGroup, selectedGroupId, lastExec, providerHealth, cortexLast, inhLast, tomStats, affectLast, cortexFlags, memoryStats]);

  const overallProblems = nodes.filter((n) => n.status === "error" || n.status === "warn");

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-headline font-bold tracking-tight">Fluxo Tempo Real</h1>
          <p className="text-muted-foreground mt-1">
            Acompanhe o caminho de cada mensagem pelo pipeline da Syra. Atualiza a cada 5s.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Selecione uma organização" />
            </SelectTrigger>
            <SelectContent>
              {orgs?.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  <span className="flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {o.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedGroupId} onValueChange={setSelectedGroupId} disabled={!selectedOrgId}>
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Selecione um grupo para monitorar" />
            </SelectTrigger>
            <SelectContent>
              {groups?.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  <span className="flex items-center gap-2">
                    {g.instance_status === "connected"
                      ? <Wifi className="h-3.5 w-3.5 text-primary" />
                      : <WifiOff className="h-3.5 w-3.5 text-destructive" />}
                    {g.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dataUpdatedAt > 0 && (
            <Badge variant="secondary" className="gap-1.5">
              <Activity className="h-3 w-3 animate-pulse" />
              ao vivo
            </Badge>
          )}
        </div>
      </header>

      {/* Cortex flags toggle bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <span className="text-sm font-medium text-muted-foreground">Camada Cortex:</span>
            {[
              { key: "cortex_full_loop_enabled", label: "Full Loop" },
              { key: "cortex_threat_detection", label: "Threat" },
              { key: "cortex_conflict_detection", label: "Conflict" },
              { key: "cortex_input_gating", label: "Gating" },
              { key: "cortex.tom", label: "ToM" },
              { key: "cortex.affect", label: "Affect" },
            ].map((f) => {
              const flag = cortexFlags?.[f.key];
              const on = !!flag?.enabled;
              const pct = flag?.rollout_pct ?? null;
              const busy = flagBusy === f.key;
              const disabled = !orgId || !canManage || busy;
              return (
                <TooltipProvider key={f.key} delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`flag-${f.key}`}
                          checked={on}
                          disabled={disabled}
                          onCheckedChange={(next) => toggleFlag(f.key, next)}
                        />
                        <Label
                          htmlFor={`flag-${f.key}`}
                          className={`text-sm cursor-pointer ${on ? "" : "text-muted-foreground"}`}
                        >
                          {f.label}
                          {on && pct !== null && pct < 100 && (
                            <span className="ml-1 text-xs text-muted-foreground">({pct}%)</span>
                          )}
                        </Label>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {!orgId
                        ? "Selecione uma organização"
                        : !canManage
                        ? "Apenas owner/admin/superadmin podem alternar flags"
                        : `${f.key} · ${on ? "ativado" : "desativado"}`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Status geral */}
      {selectedGroupId && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatusTile
            label="Instância WhatsApp"
            status={selectedGroup?.instance_status === "connected" ? "ok" : "error"}
            value={selectedGroup?.instance_status ?? "—"}
          />
          <StatusTile
            label="Última mensagem"
            status={lastExec ? (Date.now() - new Date(lastExec.created_at).getTime() < 60_000 ? "ok" : "idle") : "idle"}
            value={lastExec ? new Date(lastExec.created_at).toLocaleTimeString() : "Sem dados"}
          />
          <StatusTile
            label="Skill executada"
            status={lastExec?.skill_name ? "ok" : "idle"}
            value={lastExec?.skill_name ?? "—"}
          />
          <StatusTile
            label="Provedores LLM"
            status={
              providerHealth?.length
                ? providerHealth.every((p) => p.status === "healthy") ? "ok"
                : providerHealth.some((p) => p.status === "healthy") ? "warn"
                : "error"
                : "idle"
            }
            value={`${providerHealth?.filter((p) => p.status === "healthy").length ?? 0}/${providerHealth?.length ?? 0} saudáveis`}
          />
        </div>
      )}

      {/* Diagrama */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Pipeline da Mensagem
            {overallProblems.length > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                {overallProblems.length} alerta(s)
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <svg viewBox="0 0 1440 640" preserveAspectRatio="xMidYMid meet" className="w-full h-auto">
              <defs>
                <marker id="arrow-active" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="hsl(var(--primary))" />
                </marker>
                <marker id="arrow-idle" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="hsl(var(--muted-foreground) / 0.4)" />
                </marker>
                <marker id="arrow-influence" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="hsl(var(--muted-foreground) / 0.7)" />
                </marker>
                <linearGradient id="tom-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary) / 0.28)" />
                  <stop offset="100%" stopColor="hsl(var(--primary) / 0.1)" />
                </linearGradient>
              </defs>

              {/* Edges */}
              {edges.map((e, i) => {
                const a = nodes.find((n) => n.id === e.from)!;
                const b = nodes.find((n) => n.id === e.to)!;
                if (!a || !b) return null;
                const isInfluence = e.variant === "influence";
                const stroke = isInfluence
                  ? "hsl(var(--muted-foreground) / 0.7)"
                  : e.active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground) / 0.4)";
                const dx = b.x - a.x;
                const dy = b.y - a.y;
                const len = Math.hypot(dx, dy) || 1;
                const off = 70;
                const x1 = a.x + (dx / len) * off;
                const y1 = a.y + (dy / len) * off;
                const x2 = b.x - (dx / len) * off;
                const y2 = b.y - (dy / len) * off;
                const markerId = isInfluence ? "arrow-influence" : (e.active ? "arrow-active" : "arrow-idle");
                const dash = isInfluence ? "3 3" : (e.active ? "0" : "6 4");
                return (
                  <g key={i} opacity={isInfluence ? 0.75 : 1}>
                    <line
                      x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke={stroke}
                      strokeWidth={isInfluence ? 1.25 : (e.active ? 2.5 : 1.5)}
                      strokeDasharray={dash}
                      markerEnd={`url(#${markerId})`}
                    />
                    {!isInfluence && e.active && (
                      <circle r="4" fill="hsl(var(--primary))">
                        <animateMotion dur="2s" repeatCount="indefinite" path={`M ${x1} ${y1} L ${x2} ${y2}`} />
                      </circle>
                    )}
                  </g>
                );
              })}

              {/* Nodes */}
              {nodes.map((n) => {
                const c = STATUS_COLORS[n.status];
                const isAlert = n.status === "error" || n.status === "warn";
                const isTom = n.id === "tom";
                const isInh = n.id === "inh";
                const fillVal = isTom ? "url(#tom-grad)" : c.fill;
                const strokeWidthVal = isTom || isInh ? 2.5 : 2;
                const statusLabel =
                  n.status === "error" ? "Erro" :
                  n.status === "warn" ? "Atenção" :
                  n.status === "ok" ? "Saudável" : "Inativo";
                return (
                  <TooltipProvider key={n.id} delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <g style={{ cursor: "pointer" }}>
                          {/* halo pulsante para erros */}
                          {isAlert && (
                            <circle cx={n.x} cy={n.y} r="60" fill={c.ring}>
                              <animate attributeName="r" values="60;72;60" dur="1.6s" repeatCount="indefinite" />
                              <animate attributeName="opacity" values="0.5;0.15;0.5" dur="1.6s" repeatCount="indefinite" />
                            </circle>
                          )}
                          {/* badge flutuante para ToM */}
                          {isTom && (
                            <g>
                              <rect x={n.x - 36} y={n.y - 60} width="72" height="18" rx="9"
                                fill="hsl(var(--primary) / 0.18)" stroke="hsl(var(--primary) / 0.6)" strokeWidth="1" />
                              <text x={n.x} y={n.y - 47} textAnchor="middle" fontSize="10" fontWeight="600" fill="hsl(var(--primary))">
                                {(tomStats?.count ?? 0) > 0 ? `${tomStats?.count} perfis` : "sem dados"}
                              </text>
                            </g>
                          )}
                          <rect
                            x={n.x - 70} y={n.y - 36}
                            width="140" height="72" rx="12"
                            fill={fillVal}
                            stroke={isTom ? "hsl(var(--primary))" : c.stroke}
                            strokeWidth={strokeWidthVal}
                          />
                          {/* ícone Brain ao lado do label do ToM */}
                          {isTom && (
                            <foreignObject x={n.x - 60} y={n.y - 24} width="16" height="16">
                              <Brain className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                            </foreignObject>
                          )}
                          <text x={n.x + (isTom ? 8 : 0)} y={n.y - 10} textAnchor="middle" fontSize="13" fontWeight={isTom ? 700 : 600} fill={isTom ? "hsl(var(--primary))" : c.text}>
                            {n.label}
                          </text>
                          {n.sub && (
                            <text x={n.x} y={n.y + 10} textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))">
                              {n.sub.length > 22 ? n.sub.slice(0, 20) + "…" : n.sub}
                            </text>
                          )}
                          {n.detail && (
                            <text x={n.x} y={n.y + 26} textAnchor="middle" fontSize="9" fill="hsl(var(--muted-foreground))">
                              {n.detail.length > 28 ? n.detail.slice(0, 26) + "…" : n.detail.split("\n")[0]}
                            </text>
                          )}
                          {/* hit area transparente garante hover/click consistente */}
                          <rect
                            x={n.x - 70} y={n.y - 36}
                            width="140" height="72" rx="12"
                            fill="transparent"
                            pointerEvents="all"
                          />
                        </g>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold text-sm">{n.label}</span>
                            <Badge
                              variant={n.status === "error" ? "destructive" : "secondary"}
                              className="text-[10px] uppercase"
                              style={n.status === "warn" ? { borderColor: c.stroke, color: c.text } : undefined}
                            >
                              {statusLabel}
                            </Badge>
                          </div>
                          {n.sub && (
                            <div className="text-xs text-muted-foreground">{n.sub}</div>
                          )}
                          {n.detail && (
                            <div className="text-xs whitespace-pre-wrap break-words">
                              {n.detail}
                            </div>
                          )}
                          {isAlert && !n.detail && (
                            <div className="text-xs text-muted-foreground italic">
                              Sem detalhe adicional disponível.
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </svg>
          </div>

          {/* Legenda */}
          <div className="mt-6 flex flex-wrap gap-4 text-xs">
            <LegendDot color="hsl(var(--primary))" label="Saudável / ativo" />
            <LegendDot color="hsl(38 92% 50%)" label="Degradado / atenção" />
            <LegendDot color="hsl(var(--destructive))" label="Erro / bloqueado" />
            <LegendDot color="hsl(var(--muted-foreground) / 0.5)" label="Inativo / sem dados" />
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <span
                className="inline-block w-6 h-0"
                style={{
                  borderTop: "1.5px dashed hsl(var(--muted-foreground) / 0.7)",
                }}
              />
              Influência (leitura de estado)
            </span>
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <span className="relative inline-flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-destructive opacity-60 animate-ping" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-destructive" />
              </span>
              Alerta ativo (halo pulsante)
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
            O caminho central mostra cada turno de conversa; a faixa superior são os filtros paralelos que enriquecem o contexto
            (Intent, Sentimento, Theory of Mind, Affect); a faixa inferior é a camada Cortex (Threat / Conflict / Input Gating)
            que alimenta o Inhibitor. O Inhibitor decide se a resposta é enviada, reescrita, bloqueada ou escalada.
          </p>
        </CardContent>
      </Card>

      {/* Detalhes da última execução */}
      {lastExec && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Última execução do pipeline</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Detail label="Skill" value={lastExec.skill_name} />
            <Detail label="Intent" value={`${lastExec.intent ?? "—"} (${Math.round((lastExec.intent_confidence ?? 0) * 100)}%)`} />
            <Detail label="Modelo" value={lastExec.model_used ?? "—"} />
            <Detail label="Latência" value={`${lastExec.execution_time_ms ?? 0}ms`} />
            <Detail label="Quality score" value={(lastExec.quality_score ?? 0).toFixed(2)} />
            <Detail label="Memórias usadas" value={String(lastExec.memory_used_count ?? 0)} />
            <Detail label="Resposta" value={lastExec.was_sent_to_group ? "Enviada" : "Não enviada"} />
            <Detail label="Tamanho" value={`${lastExec.output_length ?? 0} chars`} />
          </CardContent>
        </Card>
      )}

      {/* Estatística de Intents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Distribuição de Intents
            <Badge variant="secondary" className="text-xs">
              últimos 7 dias{selectedGroupId ? " • grupo selecionado" : " • toda a org"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {intentStats.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados de intent classify no período.</p>
          ) : (
            <div className="space-y-2">
              {intentStats.map((row) => (
                <div key={row.intent} className="flex items-center gap-3">
                  <div className="w-32 text-sm font-medium truncate">{row.intent}</div>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${row.pct}%` }}
                    />
                  </div>
                  <div className="w-24 text-right text-sm tabular-nums text-muted-foreground">
                    {row.pct.toFixed(1)}% <span className="text-xs">({row.count})</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log de Erros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-destructive" />
            Log de Erros do Pipeline
            <Badge variant="secondary" className="text-xs">
              últimas 24h{selectedGroupId ? " • grupo selecionado" : " • toda a org"}
            </Badge>
            {errorLog.length > 0 && (
              <Badge variant="destructive" className="text-xs">{errorLog.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {errorLog.length === 0 ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Nenhum erro registrado nas últimas 24 horas.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Data / Hora</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead>Provider / Modelo</TableHead>
                    <TableHead>Tipo de erro</TableHead>
                    <TableHead className="text-right">Latência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errorLog.map((e) => {
                    const d = new Date(e.created_at);
                    return (
                      <TableRow key={e.id}>
                        <TableCell className="text-xs tabular-nums">
                          <div>{d.toLocaleDateString()}</div>
                          <div className="text-muted-foreground">{d.toLocaleTimeString()}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{e.skill_name ?? "—"}</div>
                          <div className="text-xs text-muted-foreground">{e.task_class}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{e.provider_used}</div>
                          <div className="text-xs text-muted-foreground">{e.model_used}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive" className="text-xs">{e.error_type ?? "unknown"}</Badge>
                          {e.reason && (
                            <div className="text-xs text-muted-foreground mt-1 max-w-[300px] truncate" title={e.reason}>
                              {e.reason}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                          {e.latency_ms ? `${e.latency_ms}ms` : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusTile({ label, status, value }: { label: string; status: NodeStatus; value: string }) {
  const c = STATUS_COLORS[status];
  const Icon = status === "ok" ? CheckCircle2 : status === "error" ? AlertTriangle : status === "warn" ? AlertTriangle : Activity;
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="text-lg font-semibold mt-1" style={{ color: c.text }}>{value}</p>
          </div>
          <Icon className="h-5 w-5" style={{ color: c.text }} />
        </div>
      </CardContent>
    </Card>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-muted-foreground">
      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium mt-0.5">{value}</p>
    </div>
  );
}
